import * as PIXI from 'pixi.js';
import { BASKET_WIDTH, BASKET_HEIGHT, STAR_SIZE, COLORS } from './configs';
// Create basket texture using Graphics rendered to texture
export function createBasketTexture(renderer: PIXI.Renderer): PIXI.Texture {
  const graphics = new PIXI.Graphics();
  // Basket body (trapezoid shape)
  graphics.fill({ color: COLORS.BASKET_BROWN });
  graphics.moveTo(-BASKET_WIDTH / 2 + 10, -BASKET_HEIGHT / 2);
  graphics.lineTo(BASKET_WIDTH / 2 - 10, -BASKET_HEIGHT / 2);
  graphics.lineTo(BASKET_WIDTH / 2, BASKET_HEIGHT / 2);
  graphics.lineTo(-BASKET_WIDTH / 2, BASKET_HEIGHT / 2);
  graphics.closePath();
  graphics.fill();
  // Basket rim
  graphics.fill({ color: COLORS.BASKET_DARK });
  graphics.rect(-BASKET_WIDTH / 2, -BASKET_HEIGHT / 2 - 5, BASKET_WIDTH, 10);
  graphics.fill();
  // Horizontal weave pattern
  graphics.stroke({ width: 2, color: COLORS.BASKET_DARK, alpha: 0.5 });
  for (let i = 0; i < 5; i++) {
    const y = -BASKET_HEIGHT / 2 + 10 + i * 8;
    graphics.moveTo(-BASKET_WIDTH / 2 + 5, y);
    graphics.lineTo(BASKET_WIDTH / 2 - 5, y);
  }
  // Vertical weave
  for (let i = 0; i < 4; i++) {
    const x = -BASKET_WIDTH / 2 + 20 + i * 15;
    graphics.moveTo(x, -BASKET_HEIGHT / 2 + 5);
    graphics.lineTo(x - 5, BASKET_HEIGHT / 2 - 5);
  }
  const texture = PIXI.RenderTexture.create({
    width: BASKET_WIDTH,
    height: BASKET_HEIGHT + 10,
    resolution: renderer.resolution,
  });
  renderer.render({ container: graphics, target: texture, clear: true });
  graphics.destroy();
  return texture;
}
// Create star texture using Graphics with proper 5-point star
export function createStarTexture(renderer: PIXI.Renderer): PIXI.Texture {
  const graphics = new PIXI.Graphics();
  const size = STAR_SIZE;
  // Draw star using filled polygon
  const starPoints = calculateStarPoints(0, 0, 5, size * 0.5, size * 0.25);
  // Star glow (outer glow effect)
  graphics.fill({ color: COLORS.STAR_GLOW, alpha: 0.3 });
  const glowPoints = calculateStarPoints(0, 0, 5, size * 0.65, size * 0.3);
  graphics.poly(glowPoints);
  graphics.fill();
  // Star body (main star)
  graphics.fill({ color: COLORS.STAR_YELLOW });
  graphics.poly(starPoints);
  graphics.fill();
  // Star highlight (center shine)
  graphics.fill({ color: 0xFFFFFF, alpha: 0.7 });
  graphics.circle(0, 0, size * 0.12);
  graphics.fill();
  // Small bright center
  graphics.fill({ color: 0xFFFFAA });
  graphics.circle(-size * 0.05, -size * 0.05, size * 0.05);
  graphics.fill();
  const texture = PIXI.RenderTexture.create({
    width: size * 1.5,
    height: size * 1.5,
    resolution: renderer.resolution,
  });
  renderer.render({ container: graphics, target: texture, clear: true });
  graphics.destroy();
  return texture;
}
// Calculate star polygon points (5-pointed star)
function calculateStarPoints(
  cx: number,
  cy: number,
  points: number,
  outerRadius: number,
  innerRadius: number
): number[] {
  const step = Math.PI / points;
  const result: number[] = [];
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = -Math.PI / 2 + i * step;
    result.push(cx + radius * Math.cos(angle));
    result.push(cy + radius * Math.sin(angle));
  }
  return result;
}